import type { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'

import i18n from '@/lang'

import type { ErrorContext, ErrorHandlerPort, ErrorHandlerResult } from '../ports/ErrorHandlerPort'

const { t } = i18n.global

interface InvalidField {
  field: string
  message: string
  rejectedValue: string
}

interface InvalidRequestErrorData {
  message: string
  code: string
  traceId: string
  invalidFields?: InvalidField[]
}

/**
 * Handles INVALID_REQUEST errors with field validation details
 * Formats field-specific error messages for better user experience
 */
export class LeafletValidationErrorHandler implements ErrorHandlerPort {
  canHandle(error: AxiosError, context: ErrorContext): boolean {
    const data = error.response?.data as InvalidRequestErrorData

    return !!(
      context.url?.includes('/leaflets') &&
      error.response?.status === 400 &&
      data?.code === 'INVALID_REQUEST' &&
      Array.isArray(data?.invalidFields) &&
      data.invalidFields.length > 0
    )
  }

  async handleError(error: AxiosError, context: ErrorContext): Promise<ErrorHandlerResult> {
    const data = error.response?.data as InvalidRequestErrorData
    const formattedMessage = this.formatInvalidFieldsMessage(data.invalidFields!)
    const naturalId = this.extractNaturalId(context.data)
    const toastMessage = this.formatToastMessage(formattedMessage, naturalId)

    return {
      handled: true,
      shouldShowToast: true,
      message: formattedMessage,
      shouldReject: true,
      customAction: () => {
        ElMessage({
          message: toastMessage,
          type: 'error',
          grouping: true,
          duration: 0,
          showClose: true,
          dangerouslyUseHTMLString: true
        })
      }
    }
  }

  private formatInvalidFieldsMessage(invalidFields: InvalidField[]): string {
    const fieldMessages = invalidFields.map(field => {
      const fieldName = this.getFieldDisplayName(field.field)
      return `${fieldName}: ${field.message}`
    })

    return fieldMessages.join('\n')
  }

  private getFieldDisplayName(fieldName: string): string {
    const fieldMap: Record<string, string> = {
      naturalId: t('leaflets.leafletsCreate.naturalId'),
      validFrom: t('leaflets.leafletsCreate.validFrom.label'),
      validTo: t('leaflets.leafletsCreate.validTo.label'),
      publishDate: t('leaflets.leafletsCreate.publishDate.label'),
      status: t('leaflets.leafletsCreate.status.label'),
      groupId: t('leaflets.leafletsCreate.group')
      // Add more field mappings as needed
    }

    return fieldMap[fieldName] || fieldName
  }

  /**
   * Extracts naturalId from request data if available
   * @param data - The request data from context
   * @returns naturalId string or null if not found
   */
  private extractNaturalId(data: unknown): string | null {
    if (!data) return null

    // Handle object data (most common case)
    if (typeof data === 'object' && !Array.isArray(data)) {
      const objectData = data as Record<string, unknown>
      const naturalId = objectData.naturalId
      return typeof naturalId === 'string' && naturalId.trim() ? naturalId.trim() : null
    }

    // Handle JSON string data
    if (typeof data === 'string') {
      try {
        const parsedData = JSON.parse(data)
        if (typeof parsedData === 'object' && parsedData !== null) {
          const naturalId = parsedData.naturalId
          return typeof naturalId === 'string' && naturalId.trim() ? naturalId.trim() : null
        }
      } catch {
        // Not valid JSON, ignore
      }
    }

    return null
  }

  /**
   * Formats the toast message with naturalId prefix if available
   * @param baseMessage - The base validation error message
   * @param naturalId - The naturalId if available
   * @returns Formatted toast message
   */
  private formatToastMessage(baseMessage: string, naturalId: string | null): string {
    const errorPrefix = t('notification.error')

    if (naturalId) {
      return `${errorPrefix} ${t('leaflets.validation.errorInLeafletId')}: ${naturalId}<br/>${baseMessage}`
    }

    return `${errorPrefix}: ${baseMessage}`
  }
}
